/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { Constants } from './constants';
import { mapToVerticalContentCardProps } from './utils';

import type {
  HotelDealsItemAPIResponse,
  HotelDealsCarouselItemProps,
} from '../../types';

type UseHotelDealsCarouselHandlersParams = {
  recommendationItems: HotelDealsItemAPIResponse[];
  setCarouselData: React.Dispatch<
    React.SetStateAction<HotelDealsCarouselItemProps[]>
  >;
};

export const useHotelDealsCarouselHandlers = ({
  recommendationItems,
  setCarouselData,
}: UseHotelDealsCarouselHandlersParams) => {
  const loadedCardsCount = useRef(0);
  const isAppending = useRef(false);

  // Use a ref for items to avoid stale closures in handleScroll
  const itemsRef = useRef(recommendationItems);
  useEffect(() => {
    itemsRef.current = recommendationItems;
  }, [recommendationItems]);

  const initCounters = useCallback((total: number) => {
    // We start at INITIAL_LOAD_IMAGE_COUNT because the View loads the first batch
    loadedCardsCount.current = Constants.INITIAL_LOAD_IMAGE_COUNT;
    isAppending.current = false;
  }, []);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, layoutMeasurement, contentSize } = e.nativeEvent;

      const SCROLLED_X = contentOffset.x;
      const VISIBLE_WIDTH = layoutMeasurement.width;
      const TOTAL_WIDTH = contentSize.width;

      // On iPhone SE, we need a more conservative trigger
      const triggerPoint = TOTAL_WIDTH - VISIBLE_WIDTH - 100;

      if (
        isAppending.current ||
        SCROLLED_X <= 0 ||
        SCROLLED_X < triggerPoint ||
        loadedCardsCount.current >= itemsRef.current.length
      ) {
        return;
      }

      isAppending.current = true;

      const nextBatch = itemsRef.current.slice(
        loadedCardsCount.current,
        loadedCardsCount.current + Constants.SUBSEQUENT_LOAD_IMAGE_COUNT,
      );

      if (nextBatch.length > 0) {
        setCarouselData((prev) => {
          // FINAL SAFETY: Filter out any items that already exist in the list by ID
          const existingIds = new Set(
            prev.map((p) => p.model?.details?.productid),
          );
          const filteredBatch = nextBatch.filter(
            (item) => !existingIds.has(item.details?.productid),
          );

          if (filteredBatch.length === 0) return prev;

          return [...prev, ...mapToVerticalContentCardProps(filteredBatch)];
        });

        loadedCardsCount.current += nextBatch.length;
      }

      // Small delay to allow React to finish the render cycle
      setTimeout(() => {
        isAppending.current = false;
      }, 500);
    },
    [setCarouselData], // Removed recommendationItems to prevent closure issues
  );

  return { handleScroll, initCounters };
};
